import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, addDays, startOfDay, isBefore } from 'date-fns';
import { tr, de, enUS } from 'date-fns/locale';
import { toast } from 'sonner';
import { PageSeo } from '@/components/seo/PageSeo';
import { 
  Plane, Search, Calendar, Users, ArrowRight, 
  Clock, Luggage, ChevronDown, ChevronUp, Filter,
  ArrowLeftRight, Loader2, MapPin, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { flights as flightsApi } from '../lib/api';
import { baggageBadgesForOffer } from '../lib/flightBaggage';
import { useCurrency } from '../lib/currency-context';
import FlightPrice, { FlightPriceInline } from '../components/flights/FlightPrice';
import FlightFareGroupCard from '../components/flights/FlightFareGroupCard';
import { formatFlightConnectionLabel } from '../lib/flightConnection';
import { formatFlightDuration } from '../lib/flightDuration';

const FlightSearchPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  
  // Search form state
  const [tripType, setTripType] = useState('one_way'); // one_way, round_trip
  const [departure, setDeparture] = useState('');
  const [arrival, setArrival] = useState('');
  const [departureDate, setDepartureDate] = useState(addDays(new Date(), 7));
  const [returnDate, setReturnDate] = useState(addDays(new Date(), 14));
  const [passengers, setPassengers] = useState({ adult: 1, child: 0, infant: 0 });
  const [cabinType, setCabinType] = useState('ECONOMY');
  
  // Airport autocomplete
  const [departureAirports, setDepartureAirports] = useState([]);
  const [arrivalAirports, setArrivalAirports] = useState([]);
  const [showDepartureDropdown, setShowDepartureDropdown] = useState(false);
  const [showArrivalDropdown, setShowArrivalDropdown] = useState(false);
  const [selectedDepartureAirport, setSelectedDepartureAirport] = useState(null);
  const [selectedArrivalAirport, setSelectedArrivalAirport] = useState(null);
  
  // Search results
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  /** Arama hatası: tam metin (kutuda gösterilir) */
  const [flightSearchError, setFlightSearchError] = useState(null);
  
  // Filters
  const [sortBy, setSortBy] = useState('price');
  const [showFilters, setShowFilters] = useState(false);
  const [maxPrice, setMaxPrice] = useState(null);
  const [directOnly, setDirectOnly] = useState(false);
  
  // Date locale
  const getDateLocale = () => {
    switch(i18n.language) {
      case 'tr': return tr;
      case 'de': return de;
      default: return enUS;
    }
  };
  
  const airportDisplayLabel = (airport) =>
    airport ? `${airport.city} (${airport.code})` : '';

  // Fetch airports — Airtuerk rota ağı; fromCode ile ilgili varışlar önce gelir
  const searchAirports = async (query, setAirports, opts = {}) => {
    try {
      const response = await flightsApi.getAirports((query || '').trim(), opts);
      setAirports(response.data || []);
    } catch (error) {
      console.error('Airport search error:', error);
      setAirports([]);
    }
  };
  
  // Handle departure input — seçili satırla aynı metin ise seçimi koru
  const handleDepartureChange = (value) => {
    setDeparture(value);
    if (
      selectedDepartureAirport &&
      airportDisplayLabel(selectedDepartureAirport) === value.trim()
    ) {
      searchAirports(value, setDepartureAirports);
      setShowDepartureDropdown(true);
      return;
    }
    setSelectedDepartureAirport(null);
    searchAirports(value, setDepartureAirports);
    setShowDepartureDropdown(true);
  };
  
  // Handle arrival input — kalkış seçiliyse o rota varışları önce sıralanır
  const handleArrivalChange = (value) => {
    setArrival(value);
    const fromCode = selectedDepartureAirport?.code;
    const opts = fromCode ? { fromCode } : {};
    if (
      selectedArrivalAirport &&
      airportDisplayLabel(selectedArrivalAirport) === value.trim()
    ) {
      searchAirports(value, setArrivalAirports, opts);
      setShowArrivalDropdown(true);
      return;
    }
    setSelectedArrivalAirport(null);
    searchAirports(value, setArrivalAirports, opts);
    setShowArrivalDropdown(true);
  };
  
  // Select airport
  const selectDepartureAirport = (airport) => {
    setSelectedDepartureAirport(airport);
    setDeparture(`${airport.city} (${airport.code})`);
    setShowDepartureDropdown(false);
    // Kalkış değişince varış seçimini ve listesini yenile
    setSelectedArrivalAirport(null);
    setArrival('');
    setArrivalAirports([]);
    searchAirports('', setArrivalAirports, { fromCode: airport.code });
  };
  
  const selectArrivalAirport = (airport) => {
    setSelectedArrivalAirport(airport);
    setArrival(`${airport.city} (${airport.code})`);
    setShowArrivalDropdown(false);
  };
  
  // Swap airports
  const swapAirports = () => {
    const tempAirport = selectedDepartureAirport;
    const tempValue = departure;
    
    setSelectedDepartureAirport(selectedArrivalAirport);
    setDeparture(arrival);
    
    setSelectedArrivalAirport(tempAirport);
    setArrival(tempValue);
  };
  
  // Update passenger count
  const updatePassengers = (type, delta) => {
    setPassengers(prev => {
      const newCount = Math.max(0, prev[type] + delta);
      
      // At least 1 adult required
      if (type === 'adult' && newCount < 1) return prev;
      
      // Infant count cannot exceed adult count
      if (type === 'infant' && newCount > prev.adult) return prev;
      
      // Max 9 passengers total
      const total = prev.adult + prev.child + prev.infant + delta;
      if (total > 9) return prev;
      
      return { ...prev, [type]: newCount };
    });
  };
  
  const extractApiErrorDetail = (error) => {
    const d = error?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || String(e)).join(' ');
    return '';
  };

  // Search flights
  const handleSearch = async () => {
    if (!selectedDepartureAirport || !selectedArrivalAirport) {
      toast.error('Lütfen kalkış ve varış havalimanlarını seçin');
      return;
    }
    
    setLoading(true);
    setSearchResults(null);
    setFlightSearchError(null);
    
    try {
      // Build paxes array
      const paxes = [];
      for (let i = 0; i < passengers.adult; i++) paxes.push('ADULT');
      for (let i = 0; i < passengers.child; i++) paxes.push('CHILD');
      for (let i = 0; i < passengers.infant; i++) paxes.push('INFANT');
      
      // Build origin destinations
      const originDestinations = [{
        departure: selectedDepartureAirport.code,
        arrival: selectedArrivalAirport.code,
        departure_date: format(departureDate, 'yyyy-MM-dd'),
        is_departure_city: true,
        is_arrival_city: false
      }];
      
      // Add return leg for round trip
      if (tripType === 'round_trip') {
        originDestinations.push({
          departure: selectedArrivalAirport.code,
          arrival: selectedDepartureAirport.code,
          departure_date: format(returnDate, 'yyyy-MM-dd'),
          is_departure_city: true,
          is_arrival_city: false
        });
      }
      
      const response = await flightsApi.search({
        origin_destinations: originDestinations,
        paxes: paxes,
        cabin_types: [cabinType],
        max_connection_count: directOnly ? 0 : 1
      });
      
      setSearchResults(response.data);
      
      if (response.data.offers?.length === 0) {
        const notice = response.data.search_notice;
        toast.info(notice || 'Bu kriterlere uygun uçuş bulunamadı', { duration: 10000 });
        if (notice) {
          setFlightSearchError(notice);
        }
      }
      
    } catch (error) {
      console.error('Flight search error:', error);
      const detail = extractApiErrorDetail(error);
      setFlightSearchError(
        detail
          ? `${detail}\n\n${t('flightSearch.searchDocNote3Months')}`
          : [t('flightSearch.searchFailedScheduleHint'), t('flightSearch.searchDocNote3Months')].join('\n\n')
      );
      toast.error(t('flightSearch.searchFailedTitle'));
    } finally {
      setLoading(false);
    }
  };
  
  // Get flight details for an offer
  const getFlightForOffer = (offer) => {
    if (!searchResults) return null;
    return searchResults.flights.find(f => f.flight_id === offer.flight_id);
  };
  
  // Format duration
  const formatDuration = (minutes) => formatFlightDuration(minutes);
  
  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'HH:mm');
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'd MMM', { locale: getDateLocale() });
  };
  
  // Sort and filter flight groups (paket UI)
  const getSortedFlightGroups = () => {
    if (!searchResults?.flight_groups?.length) return [];
    let groups = [...searchResults.flight_groups];

    if (maxPrice) {
      groups = groups.filter((g) => g.min_total_price <= maxPrice);
    }
    if (directOnly) {
      groups = groups.filter((g) => g.stop_count === 0);
    }

    groups.sort((a, b) => {
      if (sortBy === 'price') {
        return a.min_total_price - b.min_total_price;
      }
      if (sortBy === 'duration') {
        return (a.travel_time || 999) - (b.travel_time || 999);
      }
      if (sortBy === 'departure') {
        return new Date(a.departure_time || 0) - new Date(b.departure_time || 0);
      }
      return 0;
    });
    return groups;
  };

  // Sort and filter offers (gidiş-dönüş / eski GTS)
  const getSortedOffers = () => {
    if (!searchResults?.offers) return [];
    
    let filtered = [...searchResults.offers];
    
    // Apply price filter
    if (maxPrice) {
      filtered = filtered.filter(o => o.total_price <= maxPrice);
    }
    
    // Apply direct flight filter
    if (directOnly) {
      filtered = filtered.filter(offer => {
        const flight = getFlightForOffer(offer);
        return flight && flight.stop_count === 0;
      });
    }
    
    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'price') {
        return a.total_price - b.total_price;
      } else if (sortBy === 'duration') {
        const flightA = getFlightForOffer(a);
        const flightB = getFlightForOffer(b);
        return (flightA?.travel_time || 999) - (flightB?.travel_time || 999);
      } else if (sortBy === 'departure') {
        const flightA = getFlightForOffer(a);
        const flightB = getFlightForOffer(b);
        return new Date(flightA?.departure_time || 0) - new Date(flightB?.departure_time || 0);
      }
      return 0;
    });
    
    return filtered;
  };
  
  const handleSelectOffer = (offer) => {
    const flight = getFlightForOffer(offer);

    sessionStorage.setItem('selectedFlight', JSON.stringify({
      searchUUID: searchResults.search_uuid,
      offer,
      flight,
      passengers,
      cabinType,
      route: {
        origin: selectedDepartureAirport?.code || flight?.departure_airport || '',
        destination: selectedArrivalAirport?.code || flight?.arrival_airport || '',
      },
    }));

    navigate(`/${i18n.language}/flights/booking`);
  };

  const handleSelectFareFromGroup = (partialOffer, group) => {
    const fullOffer = searchResults.offers?.find((o) => o.offer_id === partialOffer.offer_id) || partialOffer;
    const flight = searchResults.flights?.find((f) => f.flight_id === group.flight_id) || {
      flight_id: group.flight_id,
      airline_code: group.airline_code,
      flight_number: group.flight_number,
      departure_airport: group.departure_airport,
      arrival_airport: group.arrival_airport,
      departure_time: group.departure_time,
      arrival_time: group.arrival_time,
      travel_time: group.travel_time,
      stop_count: group.stop_count,
      via: group.via,
      connection_label: group.connection_label,
      brand_name: fullOffer.brand_name,
      baggage_label: fullOffer.baggage_label,
      segments: [
        { DepartureAirport: group.departure_airport, ArrivalAirport: group.arrival_airport },
      ],
    };

    sessionStorage.setItem('selectedFlight', JSON.stringify({
      searchUUID: searchResults.search_uuid,
      offer: { ...fullOffer, ...partialOffer },
      flight,
      passengers,
      cabinType,
      route: {
        origin: selectedDepartureAirport?.code || group.departure_airport || '',
        destination: selectedArrivalAirport?.code || group.arrival_airport || '',
      },
    }));

    navigate(`/${i18n.language}/flights/booking`);
  };

  const useFareGroups = Boolean(
    searchResults?.flight_groups?.length && searchResults?.trip_type === 'ONE_WAY',
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      <PageSeo title={t('flightSearch.title')} description={t('flightSearch.subtitle')} segments={['flights']} />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-sky-600 to-blue-700 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <Plane className="w-10 h-10" />
            <h1 className="text-3xl font-bold">{t('flightSearch.title')}</h1>
          </div>
          <p className="text-sky-100 text-lg">{t('flightSearch.subtitle')}</p>
        </div>
      </div>
      
      {/* Search Form */}
      <div className="container mx-auto px-4 -mt-6">
        <Card className="shadow-xl border-0">
          <CardContent className="p-6">
            {/* Trip Type */}
            <div className="flex gap-4 mb-6">
              <Button
                variant={tripType === 'one_way' ? 'default' : 'outline'}
                onClick={() => setTripType('one_way')}
                className="rounded-full"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                {t('flightSearch.oneWay')}
              </Button>
              <Button
                variant={tripType === 'round_trip' ? 'default' : 'outline'}
                onClick={() => setTripType('round_trip')}
                className="rounded-full"
              >
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                {t('flightSearch.roundTrip')}
              </Button>
            </div>
            
            {/* Search Fields: gidiş–varış + swap yan yana; tarih/yolcu ayrı grup */}
            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:gap-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-2 min-w-0 flex-1">
                {/* Departure */}
                <div className="relative flex-1 min-w-0">
                  <Label className="text-sm text-gray-600 mb-1 block">{t('flightSearch.from')}</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={departure}
                      onChange={(e) => handleDepartureChange(e.target.value)}
                      onFocus={() => {
                        searchAirports(departure, setDepartureAirports);
                        setShowDepartureDropdown(true);
                      }}
                      placeholder={t('flightSearch.cityOrAirport')}
                      className="pl-10"
                      data-testid="departure-input"
                    />
                  </div>
                  {showDepartureDropdown && departureAirports.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                      {departureAirports.map((airport) => (
                        <div
                          key={`${airport.code}-${airport.name}`}
                          className="px-4 py-3 hover:bg-gray-100 cursor-pointer"
                          onClick={() => selectDepartureAirport(airport)}
                        >
                          <div className="font-medium">{airport.city} ({airport.code})</div>
                          <div className="text-sm text-gray-500">{airport.name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-center sm:justify-center sm:pb-2 shrink-0">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={swapAirports}
                    className="rounded-full bg-gray-100 hover:bg-gray-200 h-9 w-9"
                    aria-label={t('flightSearch.swapRoute')}
                  >
                    <ArrowLeftRight className="w-4 h-4" />
                  </Button>
                </div>

                {/* Arrival */}
                <div className="relative flex-1 min-w-0">
                  <Label className="text-sm text-gray-600 mb-1 block">{t('flightSearch.to')}</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={arrival}
                      onChange={(e) => handleArrivalChange(e.target.value)}
                      onFocus={() => {
                        const fromCode = selectedDepartureAirport?.code;
                        searchAirports(
                          arrival,
                          setArrivalAirports,
                          fromCode ? { fromCode } : {},
                        );
                        setShowArrivalDropdown(true);
                      }}
                      placeholder={t('flightSearch.cityOrAirport')}
                      className="pl-10"
                      data-testid="arrival-input"
                    />
                  </div>
                  {showArrivalDropdown && arrivalAirports.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                      {arrivalAirports.map((airport) => (
                        <div
                          key={`${airport.code}-${airport.name}`}
                          className="px-4 py-3 hover:bg-gray-100 cursor-pointer"
                          onClick={() => selectArrivalAirport(airport)}
                        >
                          <div className="font-medium">{airport.city} ({airport.code})</div>
                          <div className="text-sm text-gray-500">{airport.name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:gap-3 xl:shrink-0 xl:w-auto">
              {/* Dates */}
              <div className="w-full sm:flex-1 xl:w-[200px]">
                <Label className="text-sm text-gray-600 mb-1 block">
                  {tripType === 'round_trip' ? `${t('flightSearch.departureDate')} - ${t('flightSearch.returnDate')}` : t('flightSearch.departureDate')}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <Calendar className="w-4 h-4 mr-2" />
                      {format(departureDate, 'd MMM', { locale: getDateLocale() })}
                      {tripType === 'round_trip' && (
                        <> - {format(returnDate, 'd MMM', { locale: getDateLocale() })}</>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex">
                      <div className="p-3">
                        <Label className="text-xs text-gray-500">{t('flightSearch.departure')}</Label>
                        <CalendarComponent
                          mode="single"
                          selected={departureDate}
                          onSelect={(date) => date && setDepartureDate(date)}
                          disabled={(date) => isBefore(startOfDay(date), startOfDay(new Date()))}
                          locale={getDateLocale()}
                        />
                      </div>
                      {tripType === 'round_trip' && (
                        <div className="p-3 border-l">
                          <Label className="text-xs text-gray-500">{t('flightSearch.returnDate')}</Label>
                          <CalendarComponent
                            mode="single"
                            selected={returnDate}
                            onSelect={(date) => date && setReturnDate(date)}
                            disabled={(date) => isBefore(startOfDay(date), startOfDay(departureDate))}
                            locale={getDateLocale()}
                          />
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Passengers & Cabin */}
              <div className="w-full sm:flex-1 xl:w-[200px]">
                <Label className="text-sm text-gray-600 mb-1 block">{t('flightSearch.passengers')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <Users className="w-4 h-4 mr-2" />
                      {passengers.adult + passengers.child + passengers.infant} {t('flightSearch.passengers')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72">
                    <div className="space-y-4">
                      {/* Adult */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{t('flightSearch.adult')}</div>
                          <div className="text-xs text-gray-500">12+ {t('flightSearch.years')}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updatePassengers('adult', -1)}
                          >-</Button>
                          <span className="w-8 text-center">{passengers.adult}</span>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updatePassengers('adult', 1)}
                          >+</Button>
                        </div>
                      </div>
                      
                      {/* Child */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{t('flightSearch.child')}</div>
                          <div className="text-xs text-gray-500">2-11 {t('flightSearch.years')}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updatePassengers('child', -1)}
                          >-</Button>
                          <span className="w-8 text-center">{passengers.child}</span>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updatePassengers('child', 1)}
                          >+</Button>
                        </div>
                      </div>
                      
                      {/* Infant */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{t('flightSearch.infant')}</div>
                          <div className="text-xs text-gray-500">0-2 {t('flightSearch.years')}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updatePassengers('infant', -1)}
                          >-</Button>
                          <span className="w-8 text-center">{passengers.infant}</span>
                          <Button 
                            variant="outline" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => updatePassengers('infant', 1)}
                          >+</Button>
                        </div>
                      </div>
                      
                      <hr />
                      
                      {/* Cabin Type */}
                      <div>
                        <Label className="text-sm">{t('flightSearch.cabinClass')}</Label>
                        <Select value={cabinType} onValueChange={setCabinType}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ECONOMY">{t('flightSearch.economy')}</SelectItem>
                            <SelectItem value="BUSINESS">{t('flightSearch.business')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              </div>
            </div>
            
            {/* Bilgi + arama */}
            <div className="mt-5 space-y-4">
              {(!selectedDepartureAirport || !selectedArrivalAirport) && (
                <p className="text-sm text-gray-600 leading-normal text-left font-normal">
                  {t('flightSearch.selectAirportsFromList')}
                </p>
              )}
              <div className="flex justify-center">
                <Button 
                  onClick={handleSearch}
                  disabled={loading || !selectedDepartureAirport || !selectedArrivalAirport}
                  className="px-12 py-6 text-lg rounded-full bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700"
                  data-testid="search-flights-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('flightSearch.searching')}
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      {t('flightSearch.searchFlights')}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {flightSearchError && (
              <div
                className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"
                role="alert"
              >
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0 text-amber-600 mt-0.5" />
                  <div className="space-y-2 min-w-0">
                    <p className="font-semibold text-amber-900">{t('flightSearch.searchFailedTitle')}</p>
                    <p className="whitespace-pre-wrap leading-relaxed text-amber-900/90">{flightSearchError}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Search Results */}
      {searchResults && (
        <div className="container mx-auto px-4 py-8">
          {/* Results Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold">
                {selectedDepartureAirport?.city} → {selectedArrivalAirport?.city}
              </h2>
              <p className="text-gray-600">
                {format(departureDate, 'd MMMM yyyy', { locale: getDateLocale() })} • 
                {getSortedOffers().length} uçuş bulundu
              </p>
            </div>
            
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Sırala" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price">Fiyata Göre</SelectItem>
                  <SelectItem value="duration">Süreye Göre</SelectItem>
                  <SelectItem value="departure">Kalkış Saati</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant={directOnly ? 'default' : 'outline'}
                onClick={() => setDirectOnly(!directOnly)}
              >
                Sadece Direkt
              </Button>
            </div>
          </div>

          
          {/* Flight Cards */}
          <div className="space-y-4">
            {useFareGroups
              ? getSortedFlightGroups().map((group) => (
                  <FlightFareGroupCard
                    key={group.group_id}
                    group={group}
                    onSelectFare={handleSelectFareFromGroup}
                  />
                ))
              : getSortedOffers().map((offer) => {
              const flight = getFlightForOffer(offer);
              if (!flight) return null;
              
              const firstSegment = flight.segments?.[0];
              const lastSegment = flight.segments?.[flight.segments?.length - 1];
              
              return (
                <Card 
                  key={offer.offer_id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => handleSelectOffer(offer)}
                  data-testid={`flight-card-${offer.offer_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Flight Info */}
                      <div className="flex items-center gap-6 flex-1">
                        {/* Airline Logo */}
                        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                          <span className="text-lg font-bold text-gray-600">
                            {flight.airline_code || 'XX'}
                          </span>
                        </div>
                        
                        {/* Times */}
                        <div className="flex items-center gap-4 flex-1">
                          <div className="text-center">
                            <div className="text-2xl font-bold">{formatTime(flight.departure_time)}</div>
                            <div className="text-sm text-gray-500">{firstSegment?.DepartureAirport}</div>
                          </div>
                          
                          <div className="flex-1 flex flex-col items-center">
                            <div className="text-sm text-gray-500">{formatDuration(flight.travel_time)}</div>
                            <div className="w-full h-px bg-gray-300 my-1 relative">
                              <Plane className="w-4 h-4 text-sky-600 absolute -top-2 left-1/2 -translate-x-1/2" />
                            </div>
                            <div className="text-xs text-gray-400">
                              {formatFlightConnectionLabel(flight)}
                            </div>
                          </div>
                          
                          <div className="text-center">
                            <div className="text-2xl font-bold">{formatTime(flight.arrival_time)}</div>
                            <div className="text-sm text-gray-500">{lastSegment?.ArrivalAirport}</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Badges */}
                      <div className="flex flex-wrap gap-2">
                        {(offer.brand_name || flight.brand_name) && (
                          <Badge variant="outline">{offer.brand_name || flight.brand_name}</Badge>
                        )}
                        {baggageBadgesForOffer(offer, flight).map((bag) => (
                          <Badge
                            key={bag.key}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            <Luggage className="w-3 h-3 shrink-0" />
                            {bag.label}
                          </Badge>
                        ))}
                      </div>
                      
                      {/* Price */}
                      <div className="text-right lg:w-44">
                        <FlightPrice amountEur={offer.total_price} size="lg" className="items-end" />
                        <Button className="mt-2 w-full" data-testid={`select-flight-${offer.offer_id}`}>
                          Seç
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {(useFareGroups ? getSortedFlightGroups().length === 0 : getSortedOffers().length === 0) && (
              <div className="text-center py-12 text-gray-500">
                <Plane className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Bu kriterlere uygun uçuş bulunamadı</p>
                {searchResults.search_notice ? (
                  <p className="text-sm text-amber-800 max-w-lg mx-auto mt-3 leading-relaxed">
                    {searchResults.search_notice}
                  </p>
                ) : (
                  <p className="text-sm">Lütfen farklı tarih veya havalimanı deneyin (test için en az 3 ay sonrası)</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Loading State */}
      {loading && (
        <div className="container mx-auto px-4 py-12 text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-sky-600" />
          <p className="text-lg text-gray-600">En uygun uçuşlar aranıyor...</p>
          <p className="text-sm text-gray-400">Bu işlem birkaç saniye sürebilir</p>
        </div>
      )}
    </div>
  );
};

export default FlightSearchPage;
